import { useCallback, useEffect, useState } from "react";
import { sameAiModelChoice } from "@opencloud/agent-protocol";
import { api } from "./api.js";
import { agentBrand } from "./brand.js";
import type {
  AgentModelPicker,
  AgentRuntimeChoice,
  AiAutomaticStatus,
  AiModelChoice,
  AiModelPickerGroup,
  AiModelPickerOption,
  ConversationThread,
} from "./types.js";

/**
 * The composer's model and reasoning picker
 * (docs/product/ai-capacity/PROVIDERS-AND-MODELS.md). A pick is a provider and
 * one of its models together, and the picked provider pays. The service
 * decides what is offered, where a chat starts and whether a pick can run;
 * this module only shows that and sends a changed pick with the next message.
 */

/** Why the pick the next message would use can't run, as the composer says it. */
export interface ComposerRuntimeProblem {
  modelLabel: string;
  providerLabel: string;
  /** One plain sentence from the service. */
  message: string;
}

/** What the composer's model and reasoning picker shows. */
export interface ComposerRuntimeView {
  selectionScope?: "conversation" | "participant";
  ownerSharingAllowed?: boolean;
  /** The agent engine, named only when it is not the installation default's choice. */
  engineLabel?: string | null;
  /** Its full harness name, shown inside the picker. */
  engineFullName?: string | null;
  /** Connected providers, then trial credit, each with the models switched on for it. */
  groups: AiModelPickerGroup[];
  /** The pick the next message uses: an unsent pick, else the thread's, else the default. */
  choice: AiModelChoice | null;
  /** What the next root uses before any unsent pick: the thread's pick, else the default. */
  currentChoice: AiModelChoice | null;
  modelLabel: string | null;
  providerLabel: string | null;
  /** Why `choice` can't take new work now. */
  problem: ComposerRuntimeProblem | null;
  reasoningEfforts: AgentModelPicker["reasoningEfforts"];
  /** The chosen effort, or the conversation's default. */
  reasoningEffort: string;
  defaultReasoningEffort: string;
  /** App chats: what the app's automatic tasks use now. */
  automatic: AiAutomaticStatus | null;
  canManageProviders: boolean;
}

export function agentModelsPath(appId: string | null, threadId: string | null): string {
  const query = new URLSearchParams();
  if (appId) query.set("appId", appId);
  if (threadId) query.set("threadId", threadId);
  const search = query.toString();
  return search ? `/v1/agent-models?${search}` : "/v1/agent-models";
}

/** A light shape check; the service owns validation. */
export function isAgentModelPicker(value: unknown): value is AgentModelPicker {
  const candidate = value as Partial<AgentModelPicker> | null;
  return Boolean(
    candidate &&
      typeof candidate === "object" &&
      candidate.schemaVersion === 2 &&
      Array.isArray(candidate.groups) &&
      Array.isArray(candidate.reasoningEfforts) &&
      typeof candidate.defaultReasoningEffort === "string",
  );
}

/**
 * What the composer offers this person in this chat. A failed read retries
 * when `retryKey` changes, and `refresh()` reads it again, for example after a
 * pick was saved or refused. While a new chat gets its thread, the picker it
 * started from stays visible.
 */
export function useAgentModelPicker(input: {
  appId: string | null;
  threadId: string | null;
  enabled: boolean;
  retryKey?: unknown;
}): { picker: AgentModelPicker | null; refresh: () => void } {
  const { appId, threadId, enabled, retryKey } = input;
  const [state, setState] = useState<{
    appId: string | null;
    threadId: string | null;
    picker: AgentModelPicker | null;
  }>({ appId, threadId, picker: null });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const abort = new AbortController();
    void api<unknown>(agentModelsPath(appId, threadId), { signal: abort.signal }).then((value) => {
      if (!abort.signal.aborted && isAgentModelPicker(value)) setState({ appId, threadId, picker: value });
    }, () => undefined);
    return () => abort.abort();
  }, [appId, threadId, enabled, revision, retryKey]);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const current = state.appId === appId && (state.threadId === threadId || state.threadId === null);
  return { picker: enabled && current ? state.picker : null, refresh };
}

/** A stable key for one provider and model. */
export function choiceKey(choice: AiModelChoice): string {
  return choice.source === "included" ? `included:${choice.model}` : `${choice.connectionId}:${choice.model}`;
}

export function findPickerOption(
  groups: readonly AiModelPickerGroup[],
  choice: AiModelChoice | null | undefined,
): { group: AiModelPickerGroup; option: AiModelPickerOption } | null {
  if (!choice) return null;
  for (const group of groups) {
    const option = group.options.find((candidate) => sameAiModelChoice(candidate.choice, choice));
    if (option) return { group, option };
  }
  return null;
}

const PROVIDER_NAMES: Readonly<Record<string, string>> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  openrouter: "OpenRouter",
};

/**
 * Names a pick the picker no longer lists, such as a model since switched
 * off: the model id without its provider, and the provider's group if it is
 * still connected.
 */
function unlistedChoiceLabels(groups: readonly AiModelPickerGroup[], choice: AiModelChoice) {
  const slash = choice.model.indexOf("/");
  const provider = choice.model.slice(0, slash);
  const group = groups.find((candidate) =>
    candidate.id === (choice.source === "included" ? "included" : choice.connectionId));
  return {
    modelLabel: choice.model.slice(slash + 1),
    providerLabel: group?.label ??
      (choice.source === "included" ? `${agentBrand().productName} AI` : PROVIDER_NAMES[provider] ?? provider),
  };
}

export function composerRuntimeView(input: {
  picker: AgentModelPicker | null;
  thread: Pick<ConversationThread, "reasoningEffort"> | null;
  choice: AgentRuntimeChoice;
  engine?: { label: string; fullName: string } | null;
}): ComposerRuntimeView | null {
  const { picker, thread, choice } = input;
  if (!picker) return null;
  const currentChoice = picker.threadChoice ?? picker.defaultChoice;
  // An unsent pick the picker no longer offers (after a provider change) is dropped.
  const unsent = findPickerOption(picker.groups, choice.choice) ? choice.choice : undefined;
  const effective = unsent ?? currentChoice;
  const listed = findPickerOption(picker.groups, effective);
  const labels = listed
    ? { modelLabel: listed.option.label, providerLabel: pickerProviderLabel(listed.group) }
    : effective ? unlistedChoiceLabels(picker.groups, effective) : null;
  const problemMessage = listed ? listed.option.problem?.message ?? null
    : effective ? "It's no longer available here" : null;
  return {
    ...(picker.selectionScope ? { selectionScope: picker.selectionScope } : {}),
    ...(picker.ownerSharingAllowed !== undefined ? { ownerSharingAllowed: picker.ownerSharingAllowed } : {}),
    engineLabel: input.engine?.label ?? null,
    engineFullName: input.engine?.fullName ?? null,
    groups: picker.groups,
    choice: effective,
    currentChoice,
    modelLabel: labels?.modelLabel ?? null,
    providerLabel: labels?.providerLabel ?? null,
    problem: labels && problemMessage ? { ...labels, message: problemMessage } : null,
    reasoningEfforts: picker.reasoningEfforts,
    reasoningEffort: choice.reasoningEffort ?? (picker.selectionScope === "participant" ? null : thread?.reasoningEffort) ?? picker.defaultReasoningEffort,
    defaultReasoningEffort: picker.defaultReasoningEffort,
    automatic: picker.automatic,
    canManageProviders: picker.canManageProviders,
  };
}

type RuntimeDefaults = Pick<ComposerRuntimeView, "currentChoice" | "defaultReasoningEffort" | "selectionScope">;

function currentReasoningEffort(
  thread: Pick<ConversationThread, "reasoningEffort"> | null,
  defaults: RuntimeDefaults | null,
): string | null {
  return (defaults?.selectionScope === "participant" ? null : thread?.reasoningEffort) ?? defaults?.defaultReasoningEffort ?? null;
}

/**
 * Only choices that change what the next root uses travel with a message; the
 * service saves them with that root. Re-choosing the current value sends
 * nothing, so it never forces a new root. A pick the picker no longer offers
 * is dropped.
 */
export function runtimeChoiceToSend(
  thread: Pick<ConversationThread, "reasoningEffort"> | null,
  choice: AgentRuntimeChoice,
  view: Pick<ComposerRuntimeView, "groups"> & RuntimeDefaults,
): AgentRuntimeChoice | undefined {
  const next: AgentRuntimeChoice = {};
  if (view.selectionScope === "participant") {
    // Send exactly what the admin saw, even for the default: a concurrent
    // owner change must never silently select another paying account.
    const selected = findPickerOption(view.groups, choice.choice) ? choice.choice : view.currentChoice;
    return { ...(selected ? { choice: selected } : {}),
      reasoningEffort: choice.reasoningEffort ?? view.defaultReasoningEffort };
  }
  if (choice.choice && !sameAiModelChoice(choice.choice, view.currentChoice) &&
    findPickerOption(view.groups, choice.choice)) next.choice = choice.choice;
  if (choice.reasoningEffort != null && choice.reasoningEffort !== currentReasoningEffort(thread, view)) {
    next.reasoningEffort = choice.reasoningEffort;
  }
  return next.choice !== undefined || next.reasoningEffort !== undefined ? next : undefined;
}

export function accountOwnerLabel(group: AiModelPickerGroup): string | null {
  return group.accountOwner === "app_owner" ? "App owner's provider"
    : group.accountOwner === "you" ? "Your provider"
    : group.accountOwner === "organisation" ? "Organisation provider" : null;
}

export function pickerProviderLabel(group: AiModelPickerGroup): string {
  const owner = accountOwnerLabel(group);
  return owner ? `${group.label} · ${owner}` : group.label;
}

/** Drops unsent choices the thread now uses, whether this view sent them or not. */
export function settledRuntimeChoice(
  choice: AgentRuntimeChoice,
  thread: Pick<ConversationThread, "reasoningEffort"> | null,
  defaults: RuntimeDefaults | null,
): AgentRuntimeChoice {
  const pick = choice.choice && defaults && sameAiModelChoice(choice.choice, defaults.currentChoice)
    ? undefined : choice.choice;
  const reasoningEffort = choice.reasoningEffort === currentReasoningEffort(thread, defaults)
    ? undefined : choice.reasoningEffort;
  if (pick === choice.choice && reasoningEffort === choice.reasoningEffort) return choice;
  return {
    ...(pick !== undefined ? { choice: pick } : {}),
    ...(reasoningEffort !== undefined ? { reasoningEffort } : {}),
  };
}

/** A stored sentence as a stand-alone sentence, whether or not it ends in a full stop. */
export function asSentence(text: string): string {
  const trimmed = text.trim().replace(/\.+$/, "");
  return trimmed ? `${trimmed}.` : "";
}
